/* 
  ========================================================================
  Ejemplo de ConfirmationModal:
  ========================================================================

  1. MODAL PARA ELIMINAR (Rol, Permiso, Sucursal, etc.):
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="rol" // o permiso, sucursal, etc.
      />

  2. MODAL PARA CAMBIAR ESTATUS (Habilitar/Deshabilitar):
      <ConfirmationModal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onConfirm={executeChangeStatus}
        preset="toggleStatus"
        itemName="rol" // o usuario, sucursal, etc.
      />
*/
import { AlertCircle, Info } from 'lucide-react'
import React from 'react'

import { Button } from './button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog'

export type ModalPreset = 'delete' | 'toggleStatus' | 'cancel' | 'reject'

interface ConfirmationModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => unknown | Promise<unknown>
  preset: ModalPreset
  itemName?: string
  description?: React.ReactNode
  details?: React.ReactNode
  confirmText?: string
  cancelText?: string
  isLoading?: boolean
  loadingText?: string
}

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  preset,
  itemName = 'registro',
  description,
  details,
  confirmText,
  cancelText,
  isLoading = false,
  loadingText = 'Procesando...',
}: ConfirmationModalProps) {
  const presetsConfig = {
    delete: {
      title: `Eliminar ${itemName}`,
      description: `¿Estás seguro de que deseas eliminar este ${itemName}? Esta acción no se puede deshacer.`,
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      type: 'delete' as const,
    },
    toggleStatus: {
      title: 'Cambiar Estado',
      description: `¿Estás seguro de que deseas habilitar o deshabilitar este ${itemName}? Esto afectará su disponibilidad en el sistema.`,
      confirmText: 'Sí, cambiar estado',
      cancelText: 'Cancelar',
      type: 'status' as const,
    },
    cancel: {
      title: `Cancelar ${itemName}`,
      description: `¿Estás seguro de que deseas cancelar esta ${itemName}? Esta acción no se puede deshacer.`,
      confirmText: 'Sí, cancelar',
      cancelText: 'Volver',
      type: 'delete' as const,
    },
    reject: {
      title: `Rechazar ${itemName}`,
      description: `¿Estás seguro de que deseas rechazar esta ${itemName}?`,
      confirmText: 'Sí, rechazar',
      cancelText: 'Volver',
      type: 'delete' as const,
    },
  }

  const currentConfig = presetsConfig[preset]
  const isDelete = currentConfig.type === 'delete'

  const titleColor = isDelete ? 'text-red-600' : 'text-primary'
  const buttonVariant = isDelete ? 'destructive' : 'default'
  const Icon = isDelete ? AlertCircle : Info

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => !open && !isLoading && onClose()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle
            className={`flex items-center gap-2 text-lg font-semibold ${titleColor}`}
          >
            <Icon className="h-5 w-5" />
            {currentConfig.title}
          </DialogTitle>
          <DialogDescription className="pt-2">
            {description ?? currentConfig.description}
          </DialogDescription>
        </DialogHeader>
        {details}
        <DialogFooter className="mt-4 gap-2 sm:justify-end">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {cancelText ?? currentConfig.cancelText}
          </Button>
          <Button
            variant={buttonVariant}
            onClick={onConfirm}
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading
              ? loadingText
              : (confirmText ?? currentConfig.confirmText)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
